import { createSlice, createSelector } from '@reduxjs/toolkit'
import { createApiAsyncThunk } from '@/services/apiService'
import { addFlashMessage } from '../flash/flashMessagesSlice'
import { sanitizeFormData } from '@/utils/formData'

const URL = 'prestataires'
const getUrl = (id) => {
  const url = URL
  if(id) {
    return `${url}/${id}`
  }
  return url
}

// États initiaux
const initialState = {
  items: [],
  current: null,
  loadingStatus: {
    fetchList: false,
    fetchItem: false,
    create: false,
    update: false,
    delete: false,
  },
  requestStatus: {
    success: false,
    error: null,
    errors: {}
  },
  filters: {
    page: 1,
    limit: 10
  },
  pagination: {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  }
}

const sanitizePrestataireFormData = (data) => {
  const formData = sanitizeFormData(data)
  delete formData.nom_complet
  return formData
}

// Thunks
export const fetchPrestataires = createApiAsyncThunk(
  'prestataires/fetchPrestataires',
  async (params = {}, { api, dispatch, getState, rejectWithValue }) => {
    try {
      const response = await api
        .url(URL)
        .query(params)
        .get()
      return response
    } catch (error) {
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération des prestataires')
    }
  }
)

export const createPrestataire = createApiAsyncThunk(
  'prestataires/createPrestataire',
  async (data, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(getUrl())
        .post({prestataire: sanitizePrestataireFormData(data)})
      dispatch(addFlashMessage({type: 'success', text: "Prestataire créé avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const updatePrestataire = createApiAsyncThunk(
  'prestataires/updatePrestataire',
  async ({ id, data }, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(getUrl(id))
        .put({prestataire: sanitizePrestataireFormData(data)})
      dispatch(addFlashMessage({type: 'success', text: "Prestataire mis à jour avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const deletePrestataire = createApiAsyncThunk(
  'prestataires/deletePrestataire',
  async (prestataire, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(getUrl(prestataire.id))
        .delete()
      dispatch(addFlashMessage({type: 'success', text: "Prestataire a été supprimé avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

// Valeurs par défaut pour un prestataire vide
export const emptyPrestataire = {
  nom: '',
  prenom: '',
  email: '',
  telephone: '',
  adresse_ligne1: '',
  adresse_ligne2: '',
  adresse_ligne3: '',
  code_postal: '',
  ville: ''
}

const sanitizesPayload = (action) => Object.fromEntries(
  Object.entries(action.payload).map(([key, value]) =>
    [key, value === null ? (emptyPrestataire[key] || "") : value]
  )
)

// Slice
const prestatairesSlice = createSlice({
  name: 'prestataires',
  initialState,
  reducers: {
    addToPrestataireItems: (state, action) => {
      const items = action.payload || []
      items.forEach( item => {
        if(item) {
          const index = state.items.findIndex( existing => existing.id === item.id)
          if (index !== -1) {
            state.items[index] = item
          }
          else {
            state.items = [...state.items, item]
          }
        }
      })
    },
    setEmptyCurrentPrestataire: (state, action) => {
      const data = {
        ...emptyPrestataire,
        ...action.payload
      }
      state.current = {
        ...data
      }
      state.requestStatus = initialState.requestStatus
    },
    setCurrentPrestataire: (state, action) => {
      const sanitizedData = {
        ...emptyPrestataire,
        ...sanitizesPayload(action)
      }
      state.current = {
        ...sanitizedData
      }
      state.requestStatus = initialState.requestStatus
    },
    resetCurrentPrestataire: (state) => {
      state.current = initialState.current
      state.requestStatus = initialState.requestStatus
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetRequestStatus: (state) => {
      state.requestStatus = initialState.requestStatus
    }
  },
  extraReducers: (builder) => {
    // fetchPrestataires
    builder.addCase(fetchPrestataires.pending, (state) => {
      state.loadingStatus.fetchList = true
      state.requestStatus.error = null
    })
    builder.addCase(fetchPrestataires.fulfilled, (state, action) => {
      state.items = action.payload.prestataires
      state.pagination = {
        totalCount: action.payload.total_count,
        totalPages: action.payload.total_pages,
        currentPage: action.payload.current_page
      }
      state.loadingStatus.fetchList = false
      state.requestStatus.success = true
    })
    builder.addCase(fetchPrestataires.rejected, (state, action) => {
      state.loadingStatus.fetchList = false
      state.requestStatus.error = action.payload
    })

    // createPrestataire
    builder.addCase(createPrestataire.pending, (state) => {
      state.loadingStatus.create = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(createPrestataire.fulfilled, (state, action) => {
      state.items.unshift(action.payload)
      state.loadingStatus.create = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(createPrestataire.rejected, (state, action) => {
      state.loadingStatus.create = false
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.error = action.payload
      state.requestStatus.success = false
    })

    // updatePrestataire
    builder.addCase(updatePrestataire.pending, (state) => {
      state.loadingStatus.update = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(updatePrestataire.fulfilled, (state, action) => {
      const updated = action.payload
      const index = state.items.findIndex(item => item.id === updated.id)
      if (index !== -1) {
        state.items[index] = updated
      }
      state.loadingStatus.update = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(updatePrestataire.rejected, (state, action) => {
      state.loadingStatus.update = false
      state.requestStatus.error = action.payload
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.success = false
    })
 
    // Delete Prestataire
    builder.addCase(deletePrestataire.pending, (state) => {
      state.loadingStatus.delete = true
      state.requestStatus.error = null
    })
    builder.addCase(deletePrestataire.fulfilled, (state, action) => {
      state.loadingStatus.delete = false
      const index = state.items.findIndex( item => item.id === action.payload.id)
      if (index !== -1) {
        state.items.splice(index, 1)
      }
      state.requestStatus.success = true
    })
    builder.addCase(deletePrestataire.rejected, (state, action) => {
      state.loadingStatus.delete = false
      state.requestStatus.error = action.payload
    })
  }
})

// Sélecteurs
export const selectPrestataireById = createSelector(
  [(state) => state.prestataires.items, (_, id) => id],
  (prestataires, id) => {
    if (!id) return null
    return prestataires.find( p => p.id === id)
  }
)

export const {
  addToPrestataireItems,
  setEmptyCurrentPrestataire,
  setCurrentPrestataire,
  resetCurrentPrestataire,
  setFilters,
  resetRequestStatus
} = prestatairesSlice.actions

export default prestatairesSlice.reducer